import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Slider } from '../components/ui/slider';
import { CheckCircle, Loader2, Phone, Thermometer, Heart, Activity, Camera } from 'lucide-react';
import { toast } from 'sonner';
import FaceVitalsScanner from './FaceVitalsScanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClinicKioskPage() {
  const { clinicCode } = useParams();
  const [clinicInfo, setClinicInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [step, setStep] = useState(1); // 1: phone, 2: details, 'vitals': face scan
  const [faceVitalsResult, setFaceVitalsResult] = useState(null);
  
  const [form, setForm] = useState({
    phone: '',
    name: '',
    age: '',
    gender: '',
    chief_complaint: '',
    bp_systolic: '',
    bp_diastolic: '',
    pulse: '',
    temperature: '',
    weight: '',
    spo2: '',
    pain_scale: 0,
    additional_notes: ''
  });

  useEffect(() => {
    const fetchClinicInfo = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/kiosk/clinic/${clinicCode}`);
        if (!res.ok) throw new Error('Invalid clinic code');
        const data = await res.json();
        setClinicInfo(data);
      } catch (err) {
        setError('Invalid clinic code or clinic not found');
      } finally {
        setLoading(false);
      }
    };
    
    if (clinicCode) fetchClinicInfo();
  }, [clinicCode]);

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    if (form.phone.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.chief_complaint.trim()) {
      toast.error('Please describe your symptoms');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/kiosk/clinic-checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clinic_code: clinicCode,
          phone: form.phone,
          name: form.name,
          age: parseInt(form.age) || 0,
          gender: form.gender,
          chief_complaint: form.chief_complaint,
          bp_systolic: parseInt(form.bp_systolic) || 0,
          bp_diastolic: parseInt(form.bp_diastolic) || 0,
          pulse: parseInt(form.pulse) || 0,
          temperature: parseFloat(form.temperature) || 0,
          weight: parseFloat(form.weight) || 0,
          spo2: parseInt(form.spo2) || 0,
          pain_scale: form.pain_scale,
          additional_notes: form.additional_notes
        })
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Check-in failed');
      }
      
      const data = await res.json();
      setSuccess(data);
    } catch (err) {
      toast.error(err.message || 'Check-in failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-red-200">
          <CardContent className="p-8 text-center">
            <p className="text-red-600 font-medium">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-teal-200">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-teal-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily:'Manrope'}}>Check-in Complete!</h2>
            <p className="text-slate-600 mb-4">Thank you, {success.patient_name}</p>
            <div className="bg-teal-50 rounded-xl p-6 mb-4">
              <p className="text-sm text-teal-700 mb-1">Your Token Number</p>
              <p className="text-5xl font-bold text-teal-700">#{success.token_number}</p>
            </div>
            <p className="text-sm text-slate-500">
              {success.is_new_appointment 
                ? 'Walk-in appointment created. Please wait for your turn.'
                : 'Your details have been updated. Please wait for your turn.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-white p-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6 pt-4">
          {clinicInfo?.clinic_logo_url ? (
            <img
              src={clinicInfo.clinic_logo_url}
              alt="Clinic Logo"
              className="w-16 h-16 rounded-xl mx-auto mb-3 object-contain border border-slate-200 bg-white p-1"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl mx-auto mb-3 bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
              <span className="text-white font-bold text-2xl" style={{fontFamily:'Manrope'}}>1C</span>
            </div>
          )}
          <h1 className="text-xl font-bold text-teal-800" style={{fontFamily:'Manrope'}}>
            {clinicInfo?.clinic_name || '1Clin'}
          </h1>
          <p className="text-sm text-slate-500">Self Check-In Kiosk</p>
        </div>

        {step === 'vitals' ? (
          <FaceVitalsScanner
            clinicCode={clinicCode}
            patientPhone={form.phone}
            patientName={form.name}
            embedded
            onClose={() => setStep(2)}
            onComplete={(v) => {
              setFaceVitalsResult(v);
              setForm(f => ({
                ...f,
                bp_systolic: String(v.bp_systolic || ''),
                bp_diastolic: String(v.bp_diastolic || ''),
                pulse: String(v.heart_rate || ''),
                spo2: String(v.spo2 || ''),
              }));
              setStep(2);
            }}
          />
        ) : step === 1 ? (
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily:'Manrope'}}>
                <Phone className="w-5 h-5 text-teal-600" /> Your Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePhoneSubmit} className="space-y-4">
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    data-testid="clinic-kiosk-phone"
                    type="tel"
                    value={form.phone}
                    onChange={e => setForm({...form, phone: e.target.value.replace(/\D/g, '')})}
                    placeholder="Enter 10-digit mobile number"
                    className="h-14 text-lg mt-1"
                    maxLength={10}
                    required
                  />
                  <p className="text-xs text-slate-400 mt-1">We'll use this to find your existing records or create a new profile</p>
                </div>
                <div>
                  <Label>Full Name</Label>
                  <Input
                    data-testid="clinic-kiosk-name"
                    value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Enter your full name"
                    className="h-12 mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Age</Label>
                    <Input
                      data-testid="clinic-kiosk-age"
                      type="number"
                      value={form.age}
                      onChange={e => setForm({...form, age: e.target.value})}
                      placeholder="Years"
                      className="h-12 mt-1"
                      min="0"
                      max="150"
                    />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <select
                      data-testid="clinic-kiosk-gender"
                      value={form.gender}
                      onChange={e => setForm({...form, gender: e.target.value})}
                      className="w-full h-12 mt-1 rounded-md border border-input bg-background px-3 text-sm"
                    >
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white" 
                  data-testid="clinic-kiosk-continue-btn"
                  disabled={form.phone.length < 10}
                >
                  Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg" style={{fontFamily:'Manrope'}}>
                Tell Us About Your Visit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">What brings you in today? *</Label>
                  <Textarea
                    data-testid="clinic-kiosk-complaint"
                    value={form.chief_complaint}
                    onChange={e => setForm({...form, chief_complaint: e.target.value})}
                    placeholder="Describe your symptoms or reason for visit..."
                    className="mt-1 min-h-[100px]"
                    required
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium flex items-center gap-2 mb-2">
                    <Activity className="w-4 h-4 text-red-500" /> Pain Level
                  </Label>
                  <Slider
                    value={[form.pain_scale]}
                    onValueChange={([v]) => setForm({...form, pain_scale: v})}
                    max={10}
                    step={1}
                    className="mb-1"
                  />
                  <div className="flex justify-between text-xs text-slate-400">
                    <span>No Pain (0)</span>
                    <span className="font-bold text-slate-600">{form.pain_scale}</span>
                    <span>Worst (10)</span>
                  </div>
                </div>

                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
                    <Thermometer className="w-4 h-4 text-blue-500" /> Vitals (Optional - if you have a reading)
                  </p>

                  {/* Face Vitals Scanner Button */}
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="kiosk-face-vitals-btn"
                    onClick={() => setStep('vitals')}
                    className="w-full mb-3 h-11 border-teal-300 text-teal-700 hover:bg-teal-50"
                  >
                    <Camera className="w-4 h-4 mr-2" />
                    {faceVitalsResult ? 'Rescan Vitals with Camera' : 'Scan Vitals with Camera (30s)'}
                  </Button>
                  {faceVitalsResult && (
                    <div className="bg-teal-50 border border-teal-200 rounded-lg p-2 mb-3 text-xs text-teal-700 flex items-center gap-2">
                      <Heart className="w-3.5 h-3.5" />
                      Auto-filled: HR {faceVitalsResult.heart_rate} | BP {faceVitalsResult.bp_systolic}/{faceVitalsResult.bp_diastolic} | SpO2 {faceVitalsResult.spo2}%
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">BP Systolic</Label>
                      <Input type="number" value={form.bp_systolic} onChange={e => setForm({...form, bp_systolic: e.target.value})} className="h-10 mt-0.5" placeholder="120" />
                    </div>
                    <div>
                      <Label className="text-xs">BP Diastolic</Label>
                      <Input type="number" value={form.bp_diastolic} onChange={e => setForm({...form, bp_diastolic: e.target.value})} className="h-10 mt-0.5" placeholder="80" />
                    </div>
                    <div>
                      <Label className="text-xs">Pulse (bpm)</Label>
                      <Input type="number" value={form.pulse} onChange={e => setForm({...form, pulse: e.target.value})} className="h-10 mt-0.5" placeholder="72" />
                    </div>
                    <div>
                      <Label className="text-xs">Temperature (°F)</Label>
                      <Input type="number" step="0.1" value={form.temperature} onChange={e => setForm({...form, temperature: e.target.value})} className="h-10 mt-0.5" placeholder="98.6" />
                    </div>
                    <div>
                      <Label className="text-xs">Weight (kg)</Label>
                      <Input type="number" step="0.1" value={form.weight} onChange={e => setForm({...form, weight: e.target.value})} className="h-10 mt-0.5" placeholder="70" />
                    </div>
                    <div>
                      <Label className="text-xs">SpO2 (%)</Label>
                      <Input type="number" value={form.spo2} onChange={e => setForm({...form, spo2: e.target.value})} className="h-10 mt-0.5" placeholder="98" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-sm">Additional Notes</Label>
                  <Textarea
                    value={form.additional_notes}
                    onChange={e => setForm({...form, additional_notes: e.target.value})}
                    placeholder="Any allergies, current medications, or other information..."
                    className="mt-1 min-h-[60px]"
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="button" variant="outline" className="flex-1 h-12" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-12 bg-teal-700 hover:bg-teal-800 text-white" 
                    disabled={submitting}
                    data-testid="clinic-kiosk-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    {submitting ? 'Checking In...' : 'Check In'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400 mt-6">
          Your information is secure and will only be used for your medical care.
        </p>
      </div>
    </div>
  );
}
